import m, {Vnode} from "mithril";
import {TInterviewMessage} from "../model/TInterviewThread";
import {TInterviewThreadMainViewModel} from "../model/TInterviewThreadViewModel";
import CompletionModalView from "./CompletionModalView";
import InterviewMessageContentView from "./InterviewMessageContentView";

const USER_BUBBLE_SELECTOR = ".interview-thread-bubble.interview-thread-bubble-user.message"
    + ".d-flex.flex-column.flex-grow-1.gap-1.p-2";
const AGENT_BUBBLE_SELECTOR = ".interview-thread-bubble.interview-thread-bubble-agent.message"
    + ".flex-grow-1.p-2.position-relative";
const VOICE_PLAY_BUTTON_SELECTOR = "button.btn.btn-flat-primary.btn-sm.interview-thread-voice-play"
    + ".d-inline-flex.align-items-center.justify-content-center";
const PROCESSING_BUBBLE_SELECTOR = ".interview-thread-bubble.interview-thread-bubble-agent"
    + ".interview-thread-processing-bubble.message.d-flex.flex-column.flex-grow-1"
    + ".align-items-start.gap-1.p-2.position-relative";
const MAIN_SELECTOR = ".interview-thread-main.col-12.d-flex.flex-column.h-100";

export type TThreadMainCallbacks = {
    bindMessagesContainer: (element: HTMLDivElement) => void,
    focusInputIfRequested: (element: HTMLTextAreaElement) => void,
    onInputFocus: () => void,
    onInputBlur: () => void,
    onInputTransitionEnd: (event: TransitionEvent) => void,
    onInput: (value: string) => void,
    onInputKeydown: (event: KeyboardEvent) => Promise<void>,
    onSend: () => Promise<void>,
    onSuggestedResponse: (response: string) => Promise<void>,
    onRetry: () => Promise<void>,
    onProcessingBubbleRendered: () => void,
    onToggleVoicePlayback: (message: TInterviewMessage) => Promise<void>,
    onToggleVoiceAutoPlayback: () => Promise<void>,
    onToggleVoiceRecording: () => Promise<void>,
    onCompletionModalAction: () => void,
};

export type TThreadMainAttrs = {
    model: TInterviewThreadMainViewModel,
    callbacks: TThreadMainCallbacks,
    translate: (key: string) => string,
};

export default class ThreadMainView {

    private viewMessage(
        message: TInterviewMessage,
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
        translate: (key: string) => string,
        animate: boolean,
        showSuggestedResponses: boolean,
    ): Vnode {
        const isPlaying = model.playingMessageId === message.id;
        if (message.role === "user") {
            return m(".user-message.interview-thread-user-message.mb-4", {
                className: animate ? "interview-thread-message-enter" : "",
            }, [
                m("img.avatar.d-none.d-md-block", {
                    className: model.showAnonymized ? "filter-grayscale" : "",
                    src: model.avatarPath,
                    title: model.nick,
                    alt: translate("avatar"),
                }),
                m(".interview-thread-message", [
                    m(USER_BUBBLE_SELECTOR, [
                        m(InterviewMessageContentView, {
                            message,
                            className: "interview-thread-message-content",
                        }),
                    ]),
                ]),
            ]);
        }

        const playbackActionKey = isPlaying
            ? "interview_thread_stop_playback"
            : "interview_thread_play_message";
        return m(".chatbot-message.interview-thread-agent-message.mb-4", {
            "data-interview-message-id": message.id,
        }, [
            m(".chatbot-avatar.avatar", {title: model.agentName}),
            m(".interview-thread-message", [
                m(AGENT_BUBBLE_SELECTOR, [
                    model.ttsSupported && message.speechAvailable
                        ? m(VOICE_PLAY_BUTTON_SELECTOR, {
                            type: "button",
                            disabled: model.voicePlaybackDisabled,
                            title: translate(playbackActionKey),
                            "aria-label": translate(playbackActionKey),
                            onclick: () => callbacks.onToggleVoicePlayback(message),
                        }, [
                            m("i.bi", {
                                className: isPlaying ? "bi-stop-fill text-danger" : "bi-volume-up-fill",
                                "aria-hidden": "true",
                            }),
                        ])
                        : null,
                    m(InterviewMessageContentView, {
                        message,
                        className: "interview-thread-message-content",
                    }),
                ]),
                showSuggestedResponses && message.suggestedResponses.length > 0
                    ? m(".interview-thread-suggested-responses", {
                        role: "group",
                        "aria-label": translate("interview_thread_suggested_responses"),
                    }, message.suggestedResponses.map(response => m(
                        "button.btn.btn-outline-primary.interview-thread-suggested-response",
                        {
                            type: "button",
                            onclick: () => callbacks.onSuggestedResponse(response),
                        },
                        response,
                    )))
                    : null,
            ]),
        ]);
    }

    private viewProcessingBubble(
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
    ): Vnode {
        return m(".chatbot-message.interview-thread-agent-message.mb-4", {
            className: "interview-thread-message-enter",
            role: "status",
            "aria-live": "polite",
            oncreate: callbacks.onProcessingBubbleRendered,
            onupdate: callbacks.onProcessingBubbleRendered,
        }, [
            m(".chatbot-avatar.avatar", {title: model.agentName}),
            m(".interview-thread-message", [
                m(PROCESSING_BUBBLE_SELECTOR, [
                    m(".interview-thread-processing-message", model.processingMessage),
                    m(".interview-thread-thinking", {"aria-hidden": "true"}, [
                        m("span", "."),
                        m("span", "."),
                        m("span", "."),
                    ]),
                ]),
            ]),
        ]);
    }

    private viewConversationError(
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
        translate: (key: string) => string,
    ): Vnode|null {
        if (!model.conversationError) {
            return null;
        }

        const retryLabel = translate("interview_thread_retry");
        return m(".d-inline-flex.align-items-center.gap-2.mt-2", {
            role: "alert",
            "aria-live": "assertive",
        }, [
            m(".text-danger", model.conversationError),
            model.canRetryConversationError ? m("button.btn.btn-link.btn-sm.text-danger.p-0", {
                type: "button",
                title: retryLabel,
                "aria-label": retryLabel,
                onclick: () => callbacks.onRetry(),
            }, [
                m("i.bi.bi-arrow-clockwise", {"aria-hidden": "true"}),
            ]) : null,
        ]);
    }

    private viewPlaybackSettings(
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
        translate: (key: string) => string,
    ): Vnode|null {
        if (!model.ttsSupported) {
            return null;
        }
        const actionKey = model.voiceAutoPlaybackEnabled
            ? "interview_thread_disable_auto_playback"
            : "interview_thread_enable_auto_playback";
        const statusKey = model.voiceAutoPlaybackEnabled
            ? "interview_thread_auto_read_enabled"
            : "interview_thread_auto_read_disabled";

        return m(".interview-thread-playback-settings.px-3.w-100", [
            m(".interview-thread-playback-settings-content.d-flex.align-items-center.gap-2", [
                m(".form-check.form-switch.m-0.flex-shrink-0", [
                    m(`input#${model.autoPlaybackId}.form-check-input`, {
                        type: "checkbox",
                        role: "switch",
                        checked: model.voiceAutoPlaybackEnabled,
                        disabled: model.voicePlaybackDisabled,
                        title: translate(actionKey),
                        "aria-label": translate(actionKey),
                        onchange: callbacks.onToggleVoiceAutoPlayback,
                    }),
                ]),
                m("label.interview-thread-auto-read-label.form-check-label", {
                    for: model.autoPlaybackId,
                }, [
                    `${translate("interview_thread_auto_read")} `,
                    m("strong", translate(statusKey)),
                ]),
            ]),
        ]);
    }

    private viewMessages(
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
        translate: (key: string) => string,
    ): Vnode {
        const lastMessageIndex = model.messages.length - 1;

        return m(".interview-thread-messages.flex-grow-1.overflow-auto.p-3", {
            oncreate: vnode => callbacks.bindMessagesContainer(vnode.dom as HTMLDivElement),
            onupdate: vnode => callbacks.bindMessagesContainer(vnode.dom as HTMLDivElement),
        }, [
            model.loading
                ? m(".text-muted", {role: "status", "aria-live": "polite"}, translate("interview_thread_loading"))
                : model.messages.map((message, index) => this.viewMessage(
                    message,
                    model,
                    callbacks,
                    translate,
                    model.showProcessingBubble && message.role === "user" && index === lastMessageIndex,
                    model.showSuggestedResponses && index === lastMessageIndex,
                )),
            model.showProcessingBubble ? this.viewProcessingBubble(model, callbacks) : null,
            model.threadStatusMessage
                ? m(".text-muted.mt-2", {role: "status"}, model.threadStatusMessage)
                : null,
            this.viewConversationError(model, callbacks, translate),
        ]);
    }

    private viewConversationActions(
        model: TInterviewThreadMainViewModel,
        voiceInputBusy: boolean,
        translate: (key: string) => string,
    ): Vnode|null {
        if (!model.showConversationActions) {
            return null;
        }
        const contactModeratorLabel = translate("interview_thread_help_contact_moderator");

        return m(".dropdown.dropup.interview-thread-input-menu", [
            m("button.btn.btn-sm.btn-flat-primary.interview-thread-input-menu-button", {
                type: "button",
                disabled: voiceInputBusy,
                onpointerdown: (event: PointerEvent) => event.preventDefault(),
                "data-bs-toggle": "dropdown",
                "aria-expanded": "false",
                "aria-label": translate("actions"),
                title: translate("actions"),
            }, [m("i.bi.bi-three-dots-vertical")]),
            m("ul.dropdown-menu.dropdown-menu-start.mb-1", [
                model.restartConversationUrl
                    ? m("li", [m("a.dropdown-item", {
                        href: model.restartConversationUrl,
                    }, translate("interview_thread_help_restart"))])
                    : null,
                m("li", [
                    model.contactModeratorUrl
                        ? m("a.dropdown-item", {
                            href: model.contactModeratorUrl,
                            target: "_blank",
                            rel: "noopener noreferrer",
                        }, contactModeratorLabel)
                        : m("span.dropdown-item.disabled", {
                            "aria-disabled": "true",
                        }, contactModeratorLabel),
                ]),
            ]),
        ]);
    }

    private viewVoiceStatus(
        model: TInterviewThreadMainViewModel,
        translate: (key: string) => string,
    ): Vnode|null {
        const statusKey = model.requestingVoicePermission
            ? "interview_thread_requesting_voice_permission"
            : model.recordingVoice
                ? "interview_thread_recording_voice"
                : model.transcribingVoice
                    ? "interview_thread_transcribing_voice"
                    : null;
        if (statusKey === null) {
            return null;
        }

        return m(
            ".interview-thread-voice-status.d-inline-flex.align-items-center.gap-1.me-1.text-muted.small",
            {role: "status", "aria-live": "polite"},
            [
                model.recordingVoice ? m("span.interview-thread-recording-dot") : null,
                m("span", translate(statusKey)),
            ],
        );
    }

    private viewRecordButton(
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
        translate: (key: string) => string,
    ): Vnode|null {
        if (!model.voiceRecordingSupported) {
            return null;
        }
        const actionKey = model.recordingVoice
            ? "interview_thread_stop_recording"
            : model.transcribingVoice
                ? "interview_thread_transcribing_voice"
                : model.requestingVoicePermission
                    ? "interview_thread_requesting_voice_permission"
                    : "interview_thread_start_recording";
        const buttonClass = model.recordingVoice
            ? "btn-danger interview-thread-record-button-recording"
            : "btn-flat-primary";
        const buttonStyle = model.recordingVoice
            ? {"--interview-thread-recording-halo-size": `${0.25 + (model.voiceRecordingLevel * 0.4)}rem`}
            : undefined;

        return m("button.btn.interview-thread-record-button", {
            type: "button",
            className: buttonClass,
            style: buttonStyle,
            disabled: !model.canToggleVoiceRecording && !model.recordingVoice,
            title: translate(actionKey),
            "aria-label": translate(actionKey),
            onpointerdown: (event: PointerEvent) => event.preventDefault(),
            onclick: callbacks.onToggleVoiceRecording,
        }, [
            model.transcribingVoice || model.requestingVoicePermission
                ? m("span.spinner-border.spinner-border-sm", {
                    role: "status",
                    "aria-hidden": "true",
                })
                : m("i.bi", {
                    className: model.recordingVoice ? "bi-stop-fill" : "bi-mic",
                }),
        ]);
    }

    private viewInput(
        model: TInterviewThreadMainViewModel,
        callbacks: TThreadMainCallbacks,
        translate: (key: string) => string,
    ): Vnode {
        const voiceInputBusy = model.requestingVoicePermission
            || model.recordingVoice
            || model.transcribingVoice;
        const inputFieldClass = [
            model.showConversationActions ? "interview-thread-input-field-has-menu" : "",
            model.voiceRecordingSupported ? "interview-thread-input-field-has-voice-action" : "",
            model.inputExpanded ? "interview-thread-input-field-expanded" : "",
        ].filter(Boolean).join(" ");

        return m(".interview-thread-input.p-3.w-100", [
            m(".interview-thread-input-content", [
                m(".interview-thread-input-field.position-relative", {className: inputFieldClass}, [
                    m("textarea.form-control.interview-thread-input-textarea", {
                        value: model.input,
                        rows: 1,
                        placeholder: translate("interview_thread_write_message"),
                        "aria-invalid": model.inputTooLong ? "true" : undefined,
                        oncreate: vnode => callbacks.focusInputIfRequested(vnode.dom as HTMLTextAreaElement),
                        onupdate: vnode => callbacks.focusInputIfRequested(vnode.dom as HTMLTextAreaElement),
                        onfocus: callbacks.onInputFocus,
                        onblur: callbacks.onInputBlur,
                        ontransitionend: callbacks.onInputTransitionEnd,
                        oninput: event => callbacks.onInput(
                            (event.currentTarget as HTMLTextAreaElement).value,
                        ),
                        onkeydown: (event: KeyboardEvent) => callbacks.onInputKeydown(event),
                        disabled: model.inputDisabled,
                    }),
                    this.viewConversationActions(model, voiceInputBusy, translate),
                    m(".interview-thread-input-actions.d-flex.align-items-center.gap-1", [
                        this.viewVoiceStatus(model, translate),
                        this.viewRecordButton(model, callbacks, translate),
                        m("button.btn.btn-primary.interview-thread-send-button", {
                            type: "button",
                            disabled: !model.canSend,
                            onpointerdown: (event: PointerEvent) => event.preventDefault(),
                            onclick: callbacks.onSend,
                        }, [
                            m("i.bi.bi-send"),
                            m("span", translate("send")),
                        ]),
                    ]),
                ]),
                model.voiceError
                    ? m(".interview-thread-voice-status.text-danger.small.mt-2", {
                        role: "alert",
                        "aria-live": "assertive",
                    }, model.voiceError)
                    : null,
            ]),
        ]);
    }

    public view(vnode: Vnode<TThreadMainAttrs>): Vnode {
        const {model, callbacks, translate} = vnode.attrs;

        return m(MAIN_SELECTOR, {
            className: model.goalsSidebarOpen ? "interview-thread-main-with-goals" : "",
        }, [
            this.viewPlaybackSettings(model, callbacks, translate),
            this.viewMessages(model, callbacks, translate),
            this.viewInput(model, callbacks, translate),
            model.completionModal
                ? m(CompletionModalView, {
                    model: model.completionModal,
                    onAction: callbacks.onCompletionModalAction,
                })
                : null,
        ]);
    }

}
